import { Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import type { RecurrenceEndMode } from "@/api/appointments";
import {
  PRESET_ORDER,
  presetLabels,
  type RecurrenceEnd,
  type RepeatPresetId,
  type RuleCore,
} from "../lib/recurrence";
import { RecurrenceBuilder } from "./RecurrenceBuilder";

/**
 * Calendar — the Repeats row: the preset select, the end condition, and (for
 * "Custom…") the inline {@link RecurrenceBuilder}.
 *
 * 🔴 CONTROLLED AND EFFECT-FREE. Every label is derived at RENDER time from the
 * `startDate` prop — there is no effect watching it, and no state of its own
 * beyond what the parent owns. See the design note in AppointmentFormModal.tsx:
 * an effect here would fire on every hydration and on any tz/defaultDate
 * change, and would then need the flag/ref machinery its absence makes
 * unnecessary. If a change here seems to want one, the design has drifted.
 */

interface Props {
  preset: RepeatPresetId;
  onPresetChange: (next: RepeatPresetId) => void;
  /** The form's Start date (`YYYY-MM-DD`) — the anchor every label restates. */
  startDate: string;
  /** The "Custom…" rule, owned by the parent so it survives a preset round-trip. */
  customRule: RuleCore;
  onCustomRuleChange: (next: RuleCore) => void;
  end: RecurrenceEnd;
  onEndChange: (next: RecurrenceEnd) => void;
  /**
   * The rule the current selection expresses, or null for "does not repeat".
   * Derived once by the parent (which also needs it for submit and the busy
   * summary) rather than a second time here, so the two can never disagree.
   */
  rule: RuleCore | null;
  /** D16 — set when external attendees make a repeat impossible. */
  disabled?: boolean;
  /** Why it is disabled, in words. Rendered whether or not it is. */
  disabledReason?: string;
}

const END_MODES: { value: RecurrenceEndMode; label: string }[] = [
  { value: "Never", label: "Never" },
  { value: "AfterOccurrences", label: "After a number of times" },
  { value: "OnDate", label: "On a date" },
];

/**
 * Occurrence-count bounds. The lower bound is 2 because a "series" of one is a
 * one-off wearing a rule; the upper mirrors `RecurrenceLimits.MaxOccurrences`,
 * which the server enforces independently.
 */
const MIN_OCCURRENCES = 2;
const MAX_OCCURRENCES = 800;

export function RepeatControl({
  preset,
  onPresetChange,
  startDate,
  customRule,
  onCustomRuleChange,
  end,
  onEndChange,
  rule,
  disabled = false,
  disabledReason,
}: Props) {
  // Derived at render from the CURRENT start date, never written into state.
  const labels = presetLabels(startDate);
  const repeats = rule !== null;

  return (
    <div className="flex flex-col gap-space-2">
      <Select
        id="appt-repeat"
        value={preset}
        disabled={disabled}
        onChange={(e) => onPresetChange(e.target.value as RepeatPresetId)}
      >
        {PRESET_ORDER.map((id) => (
          <option key={id} value={id}>
            {labels[id]}
          </option>
        ))}
      </Select>

      {disabledReason && (
        <p className="m-0 text-caption text-gray-mid">
          {disabledReason}
        </p>
      )}

      {preset === "custom" && (
        <RecurrenceBuilder
          value={customRule}
          onChange={onCustomRuleChange}
          startDate={startDate}
          disabled={disabled}
        />
      )}

      {/* The end condition applies to whatever rule is in force, preset or
          custom, so it sits below both rather than inside the builder. */}
      {repeats && (
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="text-small text-onyx">Repeat ends</span>
          <Select
            aria-label="Repeat ends"
            className="w-auto"
            value={end.endMode}
            disabled={disabled}
            onChange={(e) =>
              onEndChange({ ...end, endMode: e.target.value as RecurrenceEndMode })
            }
          >
            {END_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>

          {end.endMode === "AfterOccurrences" && (
            <>
              <Input
                type="number"
                min={MIN_OCCURRENCES}
                max={MAX_OCCURRENCES}
                aria-label="Number of occurrences"
                className="w-[5.5rem]"
                value={String(end.endAfterOccurrences ?? 10)}
                disabled={disabled}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  onEndChange({
                    ...end,
                    endAfterOccurrences: Number.isFinite(n)
                      ? Math.min(MAX_OCCURRENCES, Math.max(MIN_OCCURRENCES, Math.trunc(n)))
                      : MIN_OCCURRENCES,
                  });
                }}
              />
              <span className="text-small text-onyx">times</span>
            </>
          )}

          {end.endMode === "OnDate" && (
            <Input
              type="date"
              aria-label="Repeat end date"
              className="w-auto"
              /* 🔴 A WALL-CALENDAR value, passed through verbatim. Never route
                 it via a timezone-aware formatter: "ends on the 30th" means the
                 30th on a calendar, and converting it would move it a day for
                 anyone west of UTC. */
              value={end.endOnLocalDate ?? ""}
              disabled={disabled}
              onChange={(e) => onEndChange({ ...end, endOnLocalDate: e.target.value })}
            />
          )}
        </div>
      )}

      {/* A series stops at the platform's 24-month horizon whichever end mode
          is chosen — there is no top-up worker, so "Never" is not forever and
          saying so here is cheaper than a surprise in eighteen months. */}
      {repeats && end.endMode === "Never" && (
        <p className="m-0 text-caption text-gray-mid">
          Repeats are scheduled up to two years ahead.
        </p>
      )}
    </div>
  );
}
